// DESCRIPTION: Frontend booking form with stepped flow for slot-based events and Stripe payment.
// Orchestrates DatePicker, SlotPicker, AttendeeForm, and PaymentForm based on event type.

import React, { useState, useEffect, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import DatePicker from './booking/DatePicker';
import SlotPicker from './booking/SlotPicker';
import AttendeeForm from './booking/AttendeeForm';
import PaymentMethodPicker from './booking/PaymentMethodPicker';

/**
 * Payment Form Component
 *
 * Renders the Stripe Payment Element and handles payment submission.
 *
 * @param {Object}   props
 * @param {Function} props.onSuccess  - Called after successful payment with the PaymentIntent result.
 * @param {Function} props.onError    - Called when payment fails with the error message.
 * @param {number}   props.bookingId  - The booking ID this payment is for (used in return URL).
 */
const PaymentForm = ( { onSuccess, onError, bookingId } ) => {
    const stripe = useStripe();
    const elements = useElements();
    const [ isProcessing, setIsProcessing ] = useState( false );
    const [ errorMessage, setErrorMessage ] = useState( '' );

    const handleSubmit = async ( event ) => {
        event.preventDefault();

        if ( ! stripe || ! elements ) {
            return;
        }

        setIsProcessing( true );
        setErrorMessage( '' );

        try {
            const { error } = await stripe.confirmPayment( {
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            } );

            if ( error ) {
                setErrorMessage( error.message );
                if ( onError ) {
                    onError( error.message );
                }
            } else {
                onSuccess();
            }
        } catch ( e ) {
            setErrorMessage( e.message );
            if ( onError ) {
                onError( e.message );
            }
        } finally {
            setIsProcessing( false );
        }
    };

    return (
        <form onSubmit={ handleSubmit } className="nb-booking-payment">
            <h3 className="nb-booking-payment__title">
                { __( 'Payment Details', 'nemtly-booking' ) }
            </h3>

            <div className="nb-booking-payment__element">
                <PaymentElement />
            </div>

            { errorMessage && (
                <div className="nb-booking-form__error-banner">
                    <p>{ errorMessage }</p>
                </div>
            ) }

            <button
                type="submit"
                disabled={ ! stripe || isProcessing }
                className="nb-booking-form__submit-btn"
            >
                { isProcessing ? (
                    <>
                        <span className="nb-booking-form__spinner" />
                        { __( 'Processing Payment...', 'nemtly-booking' ) }
                    </>
                ) : (
                    __( 'Pay Now', 'nemtly-booking' )
                ) }
            </button>
        </form>
    );
};

/**
 * Check if an event type uses slot-based scheduling.
 *
 * @param {string} eventType The event type slug.
 *
 * @return {boolean}
 */
function isSlotBased( eventType ) {
    return eventType === 'one-on-one' || eventType === 'group';
}

/**
 * Booking Form Component
 *
 * Orchestrates the booking flow:
 * - For slot-based events (one-on-one, group): DatePicker → SlotPicker → AttendeeForm
 * - For date-based events (event): AttendeeForm only (date is fixed)
 *
 * @param {Object}   props
 * @param {number}   props.eventId    - The event post ID to book.
 * @param {Function} props.onSuccess  - Called after successful booking creation with the booking data.
 * @param {Function} props.onError    - Called when booking fails with the error message.
 * @param {string}   [props.className=''] - Additional CSS class names for the form wrapper.
 */
const BookingForm = ( { eventId, onSuccess, onError, className = '' } ) => {
    // Event data
    const [ event, setEvent ] = useState( null );
    const [ availability, setAvailability ] = useState( null );

    // Slot-based booking state
    const [ bookableDates, setBookableDates ] = useState( [] );
    const [ datesLoading, setDatesLoading ] = useState( false );
    const [ selectedDate, setSelectedDate ] = useState( '' );
    const [ slots, setSlots ] = useState( [] );
    const [ slotsLoading, setSlotsLoading ] = useState( false );
    const [ selectedSlot, setSelectedSlot ] = useState( null );

    // Attendee form state
    const [ formData, setFormData ] = useState( {
        attendee_name: '',
        attendee_email: '',
        attendee_phone: '',
        booking_meta: {},
    } );
    const [ errors, setErrors ] = useState( {} );
    const [ isLoading, setIsLoading ] = useState( false );
    const [ isSubmitted, setIsSubmitted ] = useState( false );

    // Payment state
    const [ clientSecret, setClientSecret ] = useState( '' );
    const [ stripePromise, setStripePromise ] = useState( null );
    const [ bookingId, setBookingId ] = useState( null );
    const [ requiresPayment, setRequiresPayment ] = useState( false );
    const [ paymentMethods, setPaymentMethods ] = useState( null );
    const [ paymentMethod, setPaymentMethod ] = useState( 'stripe' );
    const [ successVariant, setSuccessVariant ] = useState( null );
    const [ manualInstructions, setManualInstructions ] = useState( '' );

    // Load event details on mount.
    useEffect( () => {
        if ( eventId ) {
            loadEventDetails();
        }
    }, [ eventId ] ); // eslint-disable-line react-hooks/exhaustive-deps

    // When event loads and is slot-based, fetch bookable dates.
    useEffect( () => {
        if ( event && isSlotBased( event.event_type ) ) {
            loadBookableDates();
        }
    }, [ event ] ); // eslint-disable-line react-hooks/exhaustive-deps

    // When a date is selected for slot-based events, fetch slots.
    useEffect( () => {
        if ( selectedDate && event && isSlotBased( event.event_type ) ) {
            loadSlots( selectedDate );
        }
    }, [ selectedDate ] ); // eslint-disable-line react-hooks/exhaustive-deps

    const loadEventDetails = async () => {
        try {
            const response = await apiFetch( {
                path: `/nemtly-booking/v1/events/${ eventId }`,
            } );

            if ( response.success ) {
                setEvent( response.result );
                await checkAvailability();

                const ev = response.result;
                if ( ev && ( ev.price || 0 ) > 0 ) {
                    await loadPaymentMethods();
                }
            }
        } catch ( error ) {
            if ( onError ) {
                onError( __( 'Failed to load event details', 'nemtly-booking' ) );
            }
        }
    };

    const checkAvailability = async () => {
        try {
            const response = await apiFetch( {
                path: `/nemtly-booking/v1/events/${ eventId }/availability`,
            } );

            if ( response.success ) {
                setAvailability( response.result );
            }
        } catch ( error ) {
            // Silently fail — form will still render.
        }
    };

    const loadPaymentMethods = async () => {
        try {
            const response = await apiFetch( { path: '/nemtly-booking/v1/payment-methods' } );
            if ( response.success && response.data ) {
                setPaymentMethods( response.data );
            }
        } catch ( err ) {
            setPaymentMethods( { accept_stripe: true, accept_manual_payment: false } );
        }
    };

    const loadBookableDates = async () => {
        setDatesLoading( true );
        try {
            const response = await apiFetch( {
                path: `/nemtly-booking/v1/events/${ eventId }/dates?limit=60`,
            } );

            if ( response.success ) {
                const dates = response.result?.dates || response.result || [];
                setBookableDates( Array.isArray( dates ) ? dates : [] );
            }
        } catch ( error ) {
            // Silently fail — dates will remain empty
        } finally {
            setDatesLoading( false );
        }
    };

    const loadSlots = async ( date ) => {
        setSlotsLoading( true );
        setSlots( [] );
        setSelectedSlot( null );

        try {
            const response = await apiFetch( {
                path: `/nemtly-booking/v1/events/${ eventId }/slots?date=${ date }`,
            } );

            if ( response.success ) {
                const slotData = response.result?.slots || response.result || [];
                setSlots( Array.isArray( slotData ) ? slotData : [] );
            }
        } catch ( error ) {
            // Silently fail.
        } finally {
            setSlotsLoading( false );
        }
    };

    const handleDateSelect = useCallback( ( date ) => {
        setSelectedDate( date );
        setSelectedSlot( null );
    }, [] );

    const handleSlotSelect = useCallback( ( slot ) => {
        setSelectedSlot( slot );
    }, [] );

    const handleInputChange = ( e ) => {
        const { name, value } = e.target;
        setFormData( ( prev ) => ( { ...prev, [ name ]: value } ) );

        if ( errors[ name ] ) {
            setErrors( ( prev ) => ( { ...prev, [ name ]: '' } ) );
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if ( ! formData.attendee_name.trim() ) {
            newErrors.attendee_name = __( 'Full name is required', 'nemtly-booking' );
        }

        if ( ! formData.attendee_email.trim() ) {
            newErrors.attendee_email = __( 'Email address is required', 'nemtly-booking' );
        } else if ( ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( formData.attendee_email ) ) {
            newErrors.attendee_email = __( 'Please enter a valid email address', 'nemtly-booking' );
        }

        if ( formData.attendee_phone && ! /^[+]?[1-9][\d]{0,15}$/.test( formData.attendee_phone.replace( /[\s\-()]/g, '' ) ) ) {
            newErrors.attendee_phone = __( 'Please enter a valid phone number', 'nemtly-booking' );
        }

        setErrors( newErrors );
        return Object.keys( newErrors ).length === 0;
    };

    const handleSubmit = async ( e ) => {
        e.preventDefault();

        if ( ! validateForm() ) {
            return;
        }

        setIsLoading( true );
        setErrors( {} );

        try {
            const payload = {
                event_id: eventId,
                ...formData,
            };

            // Attach slot data for slot-based events.
            if ( event && isSlotBased( event.event_type ) && selectedSlot ) {
                payload.booking_date = selectedDate;
                payload.booking_time = selectedSlot.start;
                payload.booking_end_time = selectedSlot.end;
            }

            // Determine payment method.
            if ( event && ( event.price || 0 ) > 0 && paymentMethods ) {
                const canStripe = paymentMethods.accept_stripe !== false;
                const canManual = paymentMethods.accept_manual_payment === true;
                if ( canStripe && canManual ) {
                    payload.payment_method = paymentMethod;
                } else if ( canManual ) {
                    payload.payment_method = 'manual';
                } else {
                    payload.payment_method = 'stripe';
                }
            }

            const response = await apiFetch( {
                path: '/nemtly-booking/v1/bookings',
                method: 'POST',
                data: payload,
            } );

            if ( response.success ) {
                const result = response.data || response.result;

                if ( result.requires_payment && result.client_secret && result.stripe_pk ) {
                    setStripePromise( loadStripe( result.stripe_pk ) );
                    setClientSecret( result.client_secret );
                    setBookingId( result.id );
                    setRequiresPayment( true );
                    setIsLoading( false );
                } else if ( result.payment_method === 'manual' ) {
                    setManualInstructions( result.manual_instructions || '' );
                    setSuccessVariant( 'manual' );
                    setIsSubmitted( true );
                    if ( onSuccess ) {
                        onSuccess( result, response.message );
                    }
                } else if ( result.payment_pending ) {
                    setSuccessVariant( 'payment_pending' );
                    setIsSubmitted( true );
                    if ( onSuccess ) {
                        onSuccess( result, result.message || response.message );
                    }
                } else {
                    setIsSubmitted( true );
                    if ( onSuccess ) {
                        onSuccess( result, response.message );
                    }
                }
            } else {
                throw new Error( response.message || __( 'Booking failed', 'nemtly-booking' ) );
            }
        } catch ( error ) {
            const errorMessage = error.message || __( 'Something went wrong. Please try again.', 'nemtly-booking' );
            setErrors( { submit: errorMessage } );
            if ( onError ) {
                onError( errorMessage );
            }
            setIsLoading( false );
        }
    };

    const handlePaymentSuccess = () => {
        setIsSubmitted( true );
        if ( onSuccess ) {
            onSuccess( { id: bookingId }, __( 'Payment successful! Booking confirmed.', 'nemtly-booking' ) );
        }
    };

    // -- Render states --

    // Success state.
    if ( isSubmitted ) {
        const isManual = successVariant === 'manual';
        const isPaymentPending = successVariant === 'payment_pending';

        return (
            <div className={ `nb-booking-form nb-booking-form--success ${ className }` }>
                <div className="nb-booking-form__success-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="nb-booking-form__success-title">
                    { isManual
                        ? __( 'Booking Received!', 'nemtly-booking' )
                        : isPaymentPending
                            ? __( 'Booking Saved', 'nemtly-booking' )
                            : __( 'Booking Confirmed!', 'nemtly-booking' ) }
                </h3>
                { isManual && (
                    <>
                        <p className="nb-booking-form__success-text">
                            { __( 'Please complete payment as instructed below. We will confirm your booking once payment is received.', 'nemtly-booking' ) }
                        </p>
                        { manualInstructions && (
                            <div className="nb-booking-form__manual-instructions">
                                { manualInstructions }
                            </div>
                        ) }
                    </>
                ) }
                { isPaymentPending && (
                    <p className="nb-booking-form__success-text">
                        { __( 'Your booking is saved. We will contact you to complete payment.', 'nemtly-booking' ) }
                    </p>
                ) }
                { ! isManual && ! isPaymentPending && (
                    <p className="nb-booking-form__success-text">
                        { __( 'Thank you for your booking. You will receive a confirmation email shortly.', 'nemtly-booking' ) }
                    </p>
                ) }
            </div>
        );
    }

    // Unavailable state.
    if ( availability && availability.is_available === false ) {
        return (
            <div className={ `nb-booking-form nb-booking-form--unavailable ${ className }` }>
                <div className="nb-booking-form__unavailable-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h3 className="nb-booking-form__unavailable-title">
                    { __( 'Booking Unavailable', 'nemtly-booking' ) }
                </h3>
                <p className="nb-booking-form__unavailable-text">
                    { availability.message || __( 'This event is currently full.', 'nemtly-booking' ) }
                </p>
            </div>
        );
    }

    // Payment form state.
    if ( requiresPayment && clientSecret && stripePromise ) {
        return (
            <div className={ `nb-booking-form ${ className }` }>
                { event && event.price > 0 && (
                    <div className="nb-booking-form__payment-summary">
                        <span className="nb-booking-form__payment-label">
                            { __( 'Total to pay:', 'nemtly-booking' ) }
                        </span>
                        <span className="nb-booking-form__payment-amount">
                            { event.currency_symbol || event.currency }{ event.price }
                        </span>
                    </div>
                ) }

                <Elements stripe={ stripePromise } options={ { clientSecret } }>
                    <PaymentForm
                        onSuccess={ handlePaymentSuccess }
                        onError={ ( msg ) => setErrors( { submit: msg } ) }
                        bookingId={ bookingId }
                    />
                </Elements>
            </div>
        );
    }

    // Determine if this is a slot-based event.
    const slotBased = event && isSlotBased( event.event_type );
    const showPaymentMethodPicker = event && ( event.price || 0 ) > 0
        && paymentMethods?.accept_stripe && paymentMethods?.accept_manual_payment;

    // For slot-based events, the attendee form shows only after a slot is selected.
    const showAttendeeForm = ! slotBased || ( selectedDate && selectedSlot );

    return (
        <div className={ `nb-booking-form ${ className }` }>
            { /* Price display */ }
            { event && ( event.price || 0 ) > 0 && (
                <div className="nb-booking-form__price">
                    <span className="nb-booking-form__price-amount">
                        { event.currency_symbol || event.currency }{ event.price }
                    </span>
                    { slotBased && (
                        <span className="nb-booking-form__price-per">
                            { __( '/ session', 'nemtly-booking' ) }
                        </span>
                    ) }
                </div>
            ) }

            { /* Fixed date display for date-based events */ }
            { event && ! slotBased && (
                <div className="nb-booking-form__fixed-date">
                    <div className="nb-booking-form__fixed-date-row">
                        <svg className="nb-booking-form__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{ event.event_date }</span>
                    </div>
                    { event.event_time && (
                        <div className="nb-booking-form__fixed-date-row">
                            <svg className="nb-booking-form__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{ event.event_time }{ event.timezone ? ` (${ event.timezone })` : '' }</span>
                        </div>
                    ) }
                </div>
            ) }

            { /* Step 1: Date picker for slot-based events */ }
            { slotBased && (
                <div className="nb-booking-form__step">
                    <h3 className="nb-booking-form__step-title">
                        { __( 'Select a Date', 'nemtly-booking' ) }
                    </h3>
                    <DatePicker
                        availableDates={ bookableDates }
                        selectedDate={ selectedDate }
                        onDateSelect={ handleDateSelect }
                        loading={ datesLoading }
                    />
                </div>
            ) }

            { /* Step 2: Slot picker (appears after date selection) */ }
            { slotBased && selectedDate && (
                <div className="nb-booking-form__step">
                    <h3 className="nb-booking-form__step-title">
                        { __( 'Pick a Time', 'nemtly-booking' ) }
                    </h3>
                    <SlotPicker
                        slots={ slots }
                        selectedSlot={ selectedSlot }
                        onSlotSelect={ handleSlotSelect }
                        showCapacity={ event.event_type === 'group' }
                        loading={ slotsLoading }
                    />
                </div>
            ) }

            { /* Step 3 (or Step 1 for date-based): Attendee form + submit */ }
            { showAttendeeForm && (
                <form onSubmit={ handleSubmit } className="nb-booking-form__details" noValidate>
                    { slotBased && (
                        <h3 className="nb-booking-form__step-title">
                            { __( 'Your Details', 'nemtly-booking' ) }
                        </h3>
                    ) }

                    { /* Selected date/time summary chip for slot-based */ }
                    { slotBased && selectedSlot && (
                        <div className="nb-booking-form__selection-summary">
                            <span className="nb-booking-form__selection-chip">
                                { selectedDate } &middot; { selectedSlot.start } – { selectedSlot.end }
                            </span>
                            <button
                                type="button"
                                className="nb-booking-form__selection-change"
                                onClick={ () => {
                                    setSelectedSlot( null );
                                    setSelectedDate( '' );
                                } }
                            >
                                { __( 'Change', 'nemtly-booking' ) }
                            </button>
                        </div>
                    ) }

                    <AttendeeForm
                        formData={ formData }
                        errors={ errors }
                        onChange={ handleInputChange }
                        disabled={ isLoading }
                    />

                    { /* Payment method picker */ }
                    { showPaymentMethodPicker && (
                        <PaymentMethodPicker
                            selected={ paymentMethod }
                            onSelect={ setPaymentMethod }
                            manualLabel={ paymentMethods.manual_payment_label }
                        />
                    ) }

                    { /* Submit error */ }
                    { errors.submit && (
                        <div className="nb-booking-form__error-banner">
                            <p>{ errors.submit }</p>
                        </div>
                    ) }

                    { /* Submit button */ }
                    <button
                        type="submit"
                        className="nb-booking-form__submit-btn"
                        disabled={ isLoading }
                    >
                        { isLoading ? (
                            <>
                                <span className="nb-booking-form__spinner" />
                                { __( 'Processing...', 'nemtly-booking' ) }
                            </>
                        ) : (
                            event && event.price > 0
                                ? __( 'Continue to Payment', 'nemtly-booking' )
                                : __( 'Confirm Booking', 'nemtly-booking' )
                        ) }
                    </button>

                    <p className="nb-booking-form__terms">
                        { __( 'By booking, you agree to our terms and conditions.', 'nemtly-booking' ) }
                    </p>
                </form>
            ) }
        </div>
    );
};

export default BookingForm;
